import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Users, TrendingUp, Wallet, Clock, Search, LayoutDashboard, Loader2, DollarSign, UserCheck, Mail } from "lucide-react";
import { toast } from "sonner";

const BRL = (cents) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format((cents || 0) / 100);
const dateBR = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch { return "-"; }
};

const tierLabel = (t) => t === "free_legacy" ? "Legacy (grátis)" : t === "paid" ? "Pago" : "Trial";
const tierColor = (t) => t === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      : t === "free_legacy" ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
                      : "bg-rose-500/15 text-rose-300 border-rose-500/30";

export default function AdminPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState({ items: [], total: 0, page: 1 });
  const [payments, setPayments] = useState({ items: [], total: 0, page: 1 });
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Falha ao carregar painel"))
      .finally(() => setLoading(false));
  }, [api]);

  const loadUsers = async (page = 1) => {
    const params = new URLSearchParams({ page, size: 20 });
    if (q) params.set("q", q);
    if (tierFilter) params.set("tier", tierFilter);
    try {
      const r = await api.get(`/admin/users?${params}`);
      setUsers(r.data);
    } catch { toast.error("Falha ao carregar usuários"); }
  };
  const loadPayments = async (page = 1) => {
    const params = new URLSearchParams({ page, size: 20 });
    if (payStatus) params.set("status_filter", payStatus);
    try {
      const r = await api.get(`/admin/payments?${params}`);
      setPayments(r.data);
    } catch { toast.error("Falha ao carregar pagamentos"); }
  };
  useEffect(() => { if (tab === "users") loadUsers(1); }, [tab, tierFilter]); // eslint-disable-line
  useEffect(() => { if (tab === "payments") loadPayments(1); }, [tab, payStatus]); // eslint-disable-line

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Carregando painel…</div>;
  if (!stats) return <div className="min-h-screen flex items-center justify-center text-rose-400">Acesso negado.</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 pt-8 pb-6 space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold flex items-center gap-1.5">
            <LayoutDashboard className="w-3.5 h-3.5" /> Painel do Proprietário
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1">Visão geral</h1>
          <p className="text-xs text-slate-500 mt-1">Dados agregados — nenhuma movimentação individual é exibida.</p>
        </div>
        <span data-testid="stripe-mode-badge" className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${stats.stripe_mode === "live" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
          Stripe: {stats.stripe_mode}
        </span>
      </header>

      <div className="flex gap-2 p-1 rounded-full bg-slate-900/60 border border-white/5 w-full max-w-md">
        {[
          { k: "overview", t: "Resumo" },
          { k: "users", t: "Usuários" },
          { k: "payments", t: "Pagamentos" },
        ].map((x) => (
          <button
            key={x.k}
            data-testid={`admin-tab-${x.k}`}
            onClick={() => setTab(x.k)}
            className={`flex-1 py-2 rounded-full text-xs sm:text-sm font-semibold ${tab === x.k ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-slate-900" : "text-slate-400 hover:text-white"}`}
          >{x.t}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-5 animate-fade-in">
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card icon={Users} label="Total de usuários" value={stats.users.total} color="emerald" testid="stat-users-total" />
            <Card icon={UserCheck} label="Pagos" value={stats.users.by_tier.paid} color="emerald" testid="stat-users-paid" />
            <Card icon={Mail} label="Legacy (grátis)" value={stats.users.by_tier.free_legacy} color="sky" testid="stat-users-legacy" />
            <Card icon={Clock} label="Trial" value={stats.users.by_tier.trial} color="rose" testid="stat-users-trial" />
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card icon={TrendingUp} label="Novos (24h)" value={stats.users.new_24h} color="emerald" />
            <Card icon={TrendingUp} label="Novos (7 dias)" value={stats.users.new_7d} color="emerald" />
            <Card icon={TrendingUp} label="Novos (30 dias)" value={stats.users.new_30d} color="emerald" />
          </section>

          <section className="pnc-card p-5">
            <h2 className="font-display font-bold text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-300" /> Receita confirmada
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Metric label="Receita total" value={BRL(stats.revenue.total_brl_cents)} color="emerald" testid="revenue-total" />
              <Metric label="Últimos 30 dias" value={BRL(stats.revenue.last_30d_brl_cents)} color="emerald" />
              <Metric label="Pagamentos confirmados" value={stats.revenue.paid_payments_count} />
            </div>
            <div className="pnc-divider my-4" />
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-slate-400 text-xs">Pendentes</span><p className="font-mono-nums font-bold text-amber-300">{stats.payments.pending}</p></div>
              <div><span className="text-slate-400 text-xs">Falhos</span><p className="font-mono-nums font-bold text-rose-300">{stats.payments.failed}</p></div>
              <div><span className="text-slate-400 text-xs">Movimentações no sistema</span><p className="font-mono-nums font-bold text-white">{stats.activity.transactions_total_count}</p></div>
            </div>
          </section>
        </div>
      )}

      {tab === "users" && (
        <section className="space-y-3 animate-fade-in">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="admin-user-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUsers(1)}
                placeholder="Buscar por e-mail ou nome"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-900/60 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60"
              />
            </div>
            {["", "free_legacy", "trial", "paid"].map((t) => (
              <button
                key={t || "all"}
                data-testid={`admin-tier-${t || "all"}`}
                onClick={() => setTierFilter(t)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border ${tierFilter === t ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-slate-900/50 border-white/10 text-slate-300 hover:text-white"}`}
              >{t === "" ? "Todos" : tierLabel(t)}</button>
            ))}
          </div>
          <div className="pnc-card p-2">
            {users.items.length === 0 && <p className="text-center text-slate-500 text-sm py-6">Nenhum usuário.</p>}
            {users.items.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="p-3 flex items-center gap-3 border-b border-white/5 last:border-none">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-slate-800 border border-white/5 flex items-center justify-center font-display font-bold text-emerald-300">
                  {(u.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{u.name} {u.role === "owner" && <span className="ml-1 text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">OWNER</span>}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${tierColor(u.access_tier)}`}>{tierLabel(u.access_tier)}</span>
                  <p className="text-[10px] text-slate-500 mt-1">{dateBR(u.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
          <Pager page={users.page} total={users.total} size={20} onPage={loadUsers} />
        </section>
      )}

      {tab === "payments" && (
        <section className="space-y-3 animate-fade-in">
          <div className="flex gap-2 flex-wrap">
            {["", "paid", "pending", "failed", "expired"].map((s) => (
              <button
                key={s || "all"}
                data-testid={`admin-paystatus-${s || "all"}`}
                onClick={() => setPayStatus(s)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border ${payStatus === s ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-slate-900/50 border-white/10 text-slate-300 hover:text-white"}`}
              >{s === "" ? "Todos" : s === "paid" ? "Pagos" : s === "pending" ? "Pendentes" : s === "failed" ? "Falhos" : "Expirados"}</button>
            ))}
          </div>
          <div className="pnc-card p-2">
            {payments.items.length === 0 && <p className="text-center text-slate-500 text-sm py-6">Nenhum pagamento.</p>}
            {payments.items.map((p) => (
              <div key={p.session_id} data-testid={`admin-payment-${p.session_id}`} className="p-3 border-b border-white/5 last:border-none">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white font-mono-nums">{BRL(p.amount)}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : p.payment_status === "failed" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>{p.payment_status}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 truncate">{p.session_id}</p>
                <p className="text-[11px] text-slate-500">atualizado em {dateBR(p.updated_at)}</p>
              </div>
            ))}
          </div>
          <Pager page={payments.page} total={payments.total} size={20} onPage={loadPayments} />
        </section>
      )}
    </div>
  );
}

const COLOR_MAP = {
  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/20" },
  sky: { bg: "bg-sky-500/15", text: "text-sky-300", border: "border-sky-500/20" },
  rose: { bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/20" },
};

function Card({ icon: Icon, label, value, color = "emerald", testid }) {
  const c = COLOR_MAP[color] || COLOR_MAP.emerald;
  return (
    <div data-testid={testid} className={`pnc-card p-4 border ${c.border}`}>
      <div className={`w-8 h-8 rounded-lg ${c.bg} ${c.text} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      </div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className="font-mono-nums font-bold text-xl text-white mt-0.5">{value}</p>
    </div>
  );
}

function Metric({ label, value, color = "white", testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`font-mono-nums font-bold text-lg mt-0.5 ${color === "emerald" ? "text-emerald-300" : "text-white"}`}>{value}</p>
    </div>
  );
}

function Pager({ page, total, size, onPage }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between text-xs text-slate-400">
      <span>Página {page} de {pages} ({total} itens)</span>
      <div className="flex gap-2">
        <button data-testid="pager-prev" disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-40">Anterior</button>
        <button data-testid="pager-next" disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-40">Próxima</button>
      </div>
    </div>
  );
}
